import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSettingsContext } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";

import {
	User,
	LogOut,
	LucideProps,
} from "lucide-react";

import MenuItem from "./MenuItem";

import styles from "./Header.module.css";

interface DoulaHeaderProps {
	navigation: { name: string; href: string; icon: React.ForwardRefExoticComponent<Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>> }[];
	onHandleSignOut: () => void;
}

export default function DoulaHeader({ navigation, onHandleSignOut }: DoulaHeaderProps) {
	const navigate = useNavigate();

	const [isScrolled, setIsScrolled] = useState(false);
	const { settings } = useSettingsContext();

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 10);
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	return (
		<header
			className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
				? "bg-white/80 backdrop-blur-md border-b border-gray-200 shadow-sm"
				: "bg-white/90 backdrop-blur-md border-b border-gray-200"
				}`}
		>
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex justify-between items-center h-16">
					{/* Logo and Clickable Dashboard Badge */}
					<div className="flex items-center">
						<div className="flex items-center space-x-2">
							<Link
								to="/doula-dashboard"
								className="flex items-center space-x-2"
							>
								<img
									src={settings?.logoUrl ?? "/Gaia_Logo.svg"}
									alt={settings?.logoAlt ?? "GaiaBirth"}
									className={styles.logo}
									width={130}
									height={39}
								/>
								<span className="sr-only">Doula Dashboard</span>
							</Link>
						</div>
					</div>

					{/* Desktop Navigation - Hidden on smaller screens */}
					<nav className="block">
						<ul className="hidden lg:flex items-center space-x-1">
							{navigation.map((item) => {
								return (
									<MenuItem
										key={item.name}
										title={item.name}
										href={item.href}
										icon={item.icon}
									/>
								);
							})}
						</ul>
					</nav>

					{/* Desktop Action Buttons - Hidden on smaller screens */}
					<div className="hidden lg:flex items-center space-x-4">
						<Button
							variant="outline"
							size="sm"
							onClick={onHandleSignOut}
							className={`${styles.portalBtn} border-newTheme-lightGrey text-black hover:bg-gray-50 shadow-btn-shadow hover:text-black`}
						>
							<LogOut className="h-4 w-4 mr-2" />
							Sign Out
						</Button>
						<Button
							size="sm"
							onClick={() => navigate("/doula-profile-management")}
							className={`${styles.doulaBtn} bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow`}
						>
							<User className="h-4 w-4 mr-2" />
							Profile
						</Button>
					</div>

					{/* Mobile/Tablet Menu Button - Show on lg and below */}
					<div className="lg:hidden">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => navigate("/doula-profile-management")}
							className={`${styles.doulaBtn} bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow`}
						>
							<User className="h-4 w-4 mr-2" />
							Profile
						</Button>
					</div>
				</div>
			</div>
		</header>
	)
}
